import { Injectable, DOCUMENT, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { getAccessibleTextColor } from '../utils/contrast-utils';

@Injectable()
export class SharedUtilsService {
  private document = inject<HTMLDocument>(DOCUMENT);

  /**
   * Compute the contrasted color of a color
   *
   * @param color The color
   *
   * @return The contrasted color
   */
  contrastColor(color: string): string {
    // Delegates to the canonical WCAG-AA utility so every surface computes the
    // same contrast text color for a given background. See
    // docs/adr/0001-contrast-utility-consolidation.md.
    return getAccessibleTextColor(color);
  }

  /**
   * Get a preference value by key
   * @param key The preference key
   * @returns The preference value
   */
  getPreference = async (key: string) => {
    const { value } = await Preferences.get({ key });
    return value;
  };

  /**
   * Set a preference value by key
   * @param key The preference key
   * @param value The preference value
   */
  setPreference = async (key: string, value: string) => {
    await Preferences.set({ key, value });
  };

  /**
   * Only for JSON Data
   * Get a preference value by key and property Name
   * @param key The preference key - Preference Name
   * @returns The preference value - key within the preference object.
   */
  getPreferenceByNameAndProperties = async (
    key: string,
    propertyKey: string
  ) => {
    const { value } = await Preferences.get({ key });
    // check if value string is not empty, and Json parse the value and check if the value is JSON and retur the propertyKey
    if (value && value !== '') {
      const parsedValue = JSON.parse(value);
      if (parsedValue && parsedValue[propertyKey]) {
        return parsedValue[propertyKey];
      }
    }
    return undefined;
  };

  /**
   * Update preference value by key and property Name for JSON Object
   * @param key The preference key - Preference Name
   * @param propertyKey JSON property key - key within the preference object.
   * @param propertyValue JSON property value - value of the key within the preference object.
   */
  setPreferenceByNameAndProperties = async (
    key: string,
    propertyKey: string,
    propertyValue: any
  ) => {
    const { value } = await Preferences.get({ key });
    if (value && value !== '') {
      const parsedValue = JSON.parse(value);
      if (parsedValue && parsedValue[propertyKey]) {
        parsedValue[propertyKey] = propertyValue;
        await Preferences.set({ key, value: JSON.stringify(parsedValue) });
      }
    }
    return await this.getPreferenceByNameAndProperties(key, propertyKey);
  };
}
